import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import LiveClock from '../components/LiveClock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Plus, Trash2, Globe, CheckCircle2, Clock, User, Key, Link, Copy, XCircle, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

const IPManagement = ({ user, onLogout }) => {
  const [whitelist, setWhitelist] = useState([]);
  const [accessTokens, setAccessTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [newIP, setNewIP] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [adding, setAdding] = useState(false);
  const [currentIP, setCurrentIP] = useState('');
  
  // Access Token Dialog
  const [tokenDialog, setTokenDialog] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [tokenExpiry, setTokenExpiry] = useState('24');
  const [creatingToken, setCreatingToken] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);

  useEffect(() => {
    fetchWhitelist();
    fetchCurrentIP();
    fetchAccessTokens();
  }, []);

  const fetchWhitelist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/ip/whitelist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWhitelist(response.data.whitelist);
    } catch (error) {
      toast.error('Failed to fetch IP whitelist');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessTokens = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/access-tokens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccessTokens(response.data.tokens);
    } catch (error) {
      console.error('Failed to fetch access tokens');
    } finally {
      setTokensLoading(false);
    }
  };

  const fetchCurrentIP = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ip/check`);
      setCurrentIP(response.data.ip_address);
    } catch (error) {
      console.error('Failed to fetch current IP');
    }
  };

  const handleAddIP = async (e) => {
    e.preventDefault();
    if (!newIP.trim()) {
      toast.error('Please enter an IP address');
      return;
    }

    setAdding(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/ip/whitelist`,
        { ip_address: newIP.trim(), description: newDescription.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`IP ${newIP} added to whitelist`);
      setNewIP('');
      setNewDescription('');
      fetchWhitelist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add IP');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveIP = async (ip) => {
    if (!window.confirm(`Are you sure you want to remove ${ip} from the whitelist?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/ip/whitelist/${encodeURIComponent(ip)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`IP ${ip} removed from whitelist`);
      fetchWhitelist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove IP');
    }
  };

  const handleCreateAccessToken = async (e) => {
    e.preventDefault();
    if (!tokenName.trim()) {
      toast.error('Please enter a name for the access link');
      return;
    }

    setCreatingToken(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/access-token`,
        { name: tokenName.trim(), expires_hours: parseInt(tokenExpiry) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedToken(response.data);
      toast.success('Access link created successfully!');
      fetchAccessTokens();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create access link');
    } finally {
      setCreatingToken(false);
    }
  };

  const handleRevokeToken = async (tokenValue) => {
    if (!window.confirm('Are you sure you want to revoke this access link? It will no longer work.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/access-token/${tokenValue}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Access link revoked');
      fetchAccessTokens();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access link');
    }
  };

  const handleDeleteToken = async (tokenValue) => {
    if (!window.confirm('Are you sure you want to permanently delete this access link?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/access-token/${tokenValue}/delete`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Access link deleted');
      fetchAccessTokens();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete access link');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getAccessLink = (tokenValue) => {
    return `${FRONTEND_URL}?access_token=${tokenValue}`;
  };

  const addCurrentIP = () => {
    setNewIP(currentIP);
    setNewDescription('My current IP');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h remaining`;
    }
    return `${hours}h ${minutes}m remaining`;
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-8 overflow-auto" data-testid="ip-management">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent mb-2">
                IP Access Control
              </h1>
              <p className="text-slate-600 flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-500" />
                <span>Manage whitelisted IP addresses and admin access links</span>
              </p>
            </div>
            <LiveClock />
          </div>

          {/* Current IP Info */}
          <Card className="border-0 shadow-xl bg-gradient-to-r from-blue-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-xl shadow-lg">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600">Your Current IP Address</p>
                    <p className="text-2xl font-bold text-slate-900 font-mono">{currentIP || 'Loading...'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-600">Access Granted</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Admin Access Links Section */}
          <Card className="border-0 shadow-xl border-l-4 border-l-amber-500">
            <CardHeader className="border-b bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Key className="w-5 h-5 text-amber-600" />
                  Admin Access Links
                  <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Bypass IP Restrictions
                  </span>
                </CardTitle>
                <Button
                  onClick={() => {
                    setTokenDialog(true);
                    setGeneratedToken(null);
                    setTokenName('');
                    setTokenExpiry('24');
                  }}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Generate Access Link
                </Button>
              </div>
              <p className="text-sm text-slate-600 mt-2">
                Generate secure, time-limited access links that allow admin login from any IP address.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                    <TableRow>
                      <TableHead className="font-semibold text-slate-700">Name</TableHead>
                      <TableHead className="font-semibold text-slate-700">Status</TableHead>
                      <TableHead className="font-semibold text-slate-700">Created By</TableHead>
                      <TableHead className="font-semibold text-slate-700">Expires</TableHead>
                      <TableHead className="font-semibold text-slate-700">Last Used</TableHead>
                      <TableHead className="font-semibold text-center text-slate-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tokensLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : accessTokens.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No access links created yet. Generate one to access from anywhere!
                        </TableCell>
                      </TableRow>
                    ) : (
                      accessTokens.map((token, index) => {
                        const isExpired = token.is_expired;
                        const isRevoked = !token.is_active;
                        const isValid = !isExpired && !isRevoked;
                        
                        return (
                          <TableRow key={index} className={`hover:bg-slate-50/80 transition-colors ${!isValid ? 'opacity-60' : ''}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Link className={`w-4 h-4 ${isValid ? 'text-amber-500' : 'text-slate-400'}`} />
                                <span className="font-medium">{token.name}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {isRevoked ? (
                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1 w-fit">
                                  <XCircle className="w-3 h-3" /> Revoked
                                </span>
                              ) : isExpired ? (
                                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full flex items-center gap-1 w-fit">
                                  <Clock className="w-3 h-3" /> Expired
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full flex items-center gap-1 w-fit">
                                  <CheckCircle2 className="w-3 h-3" /> Active
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">{token.created_by}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div className="text-slate-600">{formatDate(token.expires_at)}</div>
                                {isValid && (
                                  <div className="text-xs text-amber-600">{getTimeRemaining(token.expires_at)}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {token.last_used ? formatDate(token.last_used) : 'Never'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                {isValid && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => copyToClipboard(getAccessLink(token.token))}
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      title="Copy Link"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRevokeToken(token.token)}
                                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                      title="Revoke"
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteToken(token.token)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Add New IP */}
          <Card className="border-0 shadow-xl">
            <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-white">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Plus className="w-5 h-5 text-blue-600" />
                Add New IP Address
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleAddIP} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="ip">IP Address</Label>
                  <div className="flex gap-2">
                    <Input
                      id="ip"
                      placeholder="e.g., 192.168.1.1"
                      value={newIP}
                      onChange={(e) => setNewIP(e.target.value)}
                      className="h-12"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addCurrentIP}
                      className="h-12 whitespace-nowrap"
                    >
                      Use My IP
                    </Button>
                  </div>
                </div>
                <div className="flex-1 space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Input
                    id="description"
                    placeholder="e.g., Office Network, Home, etc."
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    className="h-12"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="submit"
                    disabled={adding}
                    className="h-12 px-8 bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700"
                  >
                    {adding ? 'Adding...' : 'Add to Whitelist'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Whitelist Table */}
          <Card className="border-0 shadow-xl">
            <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-white">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Shield className="w-5 h-5 text-emerald-600" />
                Whitelisted IP Addresses
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                  {whitelist.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-gradient-to-r from-slate-100 to-slate-50">
                    <TableRow>
                      <TableHead className="font-semibold text-slate-700">IP Address</TableHead>
                      <TableHead className="font-semibold text-slate-700">Description</TableHead>
                      <TableHead className="font-semibold text-slate-700">Added By</TableHead>
                      <TableHead className="font-semibold text-slate-700">Added On</TableHead>
                      <TableHead className="font-semibold text-center text-slate-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : whitelist.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No IP addresses whitelisted yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      whitelist.map((ip, index) => (
                        <TableRow key={index} className="hover:bg-slate-50/80 transition-colors">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="px-3 py-1 bg-slate-100 rounded-lg font-mono text-sm font-semibold text-slate-800">
                                {ip.ip_address}
                              </code>
                              {ip.ip_address === currentIP && (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                                  You
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-600">
                            {ip.description || '-'}
                          </TableCell>
                          <TableCell className="text-slate-600 flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            {ip.added_by}
                          </TableCell>
                          <TableCell className="text-slate-600 text-sm flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            {formatDate(ip.created_at)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveIP(ip.ip_address)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </main>

      {/* Generate Access Link Dialog */}
      <Dialog open={tokenDialog} onOpenChange={setTokenDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Generate Admin Access Link
            </DialogTitle>
            <DialogDescription>
              Create a secure, time-limited link that bypasses IP restrictions for admin access.
            </DialogDescription>
          </DialogHeader>

          {!generatedToken ? (
            <form onSubmit={handleCreateAccessToken} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="token_name">Link Name</Label>
                <Input
                  id="token_name"
                  placeholder="e.g., Travel Access, Emergency Link"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="token_expiry">Expires In</Label>
                <Select value={tokenExpiry} onValueChange={setTokenExpiry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Hour</SelectItem>
                    <SelectItem value="6">6 Hours</SelectItem>
                    <SelectItem value="12">12 Hours</SelectItem>
                    <SelectItem value="24">24 Hours (1 Day)</SelectItem>
                    <SelectItem value="72">72 Hours (3 Days)</SelectItem>
                    <SelectItem value="168">168 Hours (1 Week)</SelectItem>
                    <SelectItem value="720">720 Hours (30 Days)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Security Note:</strong> This link will allow admin login from any IP address. 
                Keep it secure and revoke it when no longer needed.
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setTokenDialog(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  disabled={creatingToken}
                >
                  {creatingToken ? 'Generating...' : 'Generate Link'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-emerald-700 mb-2">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">Access Link Created!</span>
                </div>
                <p className="text-sm text-emerald-600 mb-3">
                  Name: <strong>{generatedToken.name}</strong> • Expires in {generatedToken.expires_hours} hours
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Your Access Link</Label>
                <div className="flex gap-2">
                  <Input 
                    value={getAccessLink(generatedToken.token)} 
                    readOnly 
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => copyToClipboard(getAccessLink(generatedToken.token))}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Important:</strong> Copy this link now! You won't be able to see the full link again.
                Use this link to access the CRM from any location without IP restrictions.
              </div>

              <Button 
                className="w-full"
                onClick={() => {
                  setTokenDialog(false);
                  setGeneratedToken(null);
                }}
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IPManagement;
